import * as React from 'react';

import { IInvitationModel, IOrganizationRoleModel } from '../../interfaces/IUser';

import ApiClient from '../../services/apiClient';
import Auth from '../../stores/authentication';
import Authorization from '../../stores/Authorization';
import { CheckmateDialog } from '../../components/shared/dialog';
import CheckmateSelect from '../../components/shared/CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import { IDialogModel } from '../../interfaces/IDialog';
import { ILookupModel } from '../../interfaces/ILookup';
import { IOrganizationModel } from '../../interfaces/IOrganization';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { Navigate } from 'react-router-dom';
import { OrganizationRoleModal } from '../../components/organization/OrganizationRoleModal';
import { OrganizationTypesEnum } from '../../utilities/Constants';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import ValidateUtils from '../../shared/validations';

const _apiClient = new ApiClient();

interface IInvitationProps {
    user: IUserModel;
}

interface IInvitationState {
    currentOrganizationRole?: IOrganizationModel;
    currentRoles: ILookupModel[];
    dialog: IDialogModel;
    isLoadingZones: boolean;
    isShowSuccessMsg: boolean;
    openOrganizationRolesModal?: boolean;
    organizationsList: IOrganizationModel[];
    redirect: boolean;
    roles: ILookupModel[];
    userInvitation: IInvitationModel;
    validation: IValidation;
    zones: IOrganizationModel[];
    isNewZone: boolean;
    hasPendingInvitationVerificationDone: boolean;
    dialogClassName: string;
}

export class Invitation extends React.Component<IInvitationProps, IInvitationState> {
    constructor(props: any) {
        super(props);

        this.state = {
            currentRoles: [],
            dialog: { isShowingModal: false, isShowCancelBtn: true },
            isLoadingZones: false,
            isShowSuccessMsg: false,
            organizationsList: [],
            redirect: false,
            roles: [],
            userInvitation: { organizationRoles: [] },
            validation: {},
            zones: [],
            isNewZone: true,
            hasPendingInvitationVerificationDone: false,
            dialogClassName: '"send-invite-confirmation-dialog',
        };
    }

    componentDidMount() {
        if (!Authorization.isAuthorizedToRoute(LocalRoutes.CreateInvitation, this.props.user))
            window.location.assign(LocalRoutes.AccessDenied);

        this.loadOrganizations();
    }

    loadOrganizations = async () => {
        const res = await _apiClient.getOrganizations();
        if (res.httpResponse.status == 401) {
            window.location.reload();
            return;
        }

        if (res.errorMessage) {
            this.setState({ validation: ValidateUtils.parseErrors(res.errors, res.errorMessage) });
            return;
        }

        if (res.payload) {
            this.setState({ organizationsList: res.payload });
        }
    };

    loadZones = async (guid: string) => {
        this.setState({ isLoadingZones: true });

        const response = await _apiClient.getAssociatedOrganizationsById(guid);

        this.setState({ isLoadingZones: false });

        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
            });
            return;
        }
        if (response.payload) {
            const associatedOrganizations = response.payload;
            const associatedPortfolios = associatedOrganizations.filter(
                (org) => org.organizationType!.id == OrganizationTypesEnum.Portfolio.Value
            );
            this.setState({
                zones: associatedPortfolios,
            });
        }
    };

    handleCheckmateSelectChange = (selectedItem: any, name: string) => {
        const userInvitation = this.state.userInvitation;
        const validation = this.state.validation;
        userInvitation.organizationRoles = [];
        switch (name) {
            case 'homeOrganization': {
                const value = selectedItem && selectedItem.guid ? selectedItem.guid : '';
                this.state.organizationsList.map((homeOrganization: IOrganizationModel) => {
                    if (value == homeOrganization.guid) {
                        userInvitation.homeOrganization = homeOrganization;
                    }
                });
                this.loadZones(value);
                validation.homeOrganization = [''];
                break;
            }
            default:
                break;
        }
        this.setState({
            userInvitation: userInvitation,
            validation: validation,
            hasPendingInvitationVerificationDone: false,
        });
    };

    changeValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const name = event.target.name;
        const value = event.target.value;

        this.setChangedValue(name, value);
    };

    setChangedValue(name: string, value: any) {
        const userInvitation = this.state.userInvitation;
        const validation = this.state.validation;
        userInvitation.organizationRoles = [];
        switch (name) {
            case 'firstName':
                userInvitation.firstName = value;
                validation.firstname = [''];
                break;
            case 'lastName':
                userInvitation.lastName = value;
                validation.lastname = [''];
                break;
            case 'email': {
                let email = value as string;
                if (email) {
                    email = email.replace(/\s/g, '').trim();
                }
                userInvitation.email = email;
                userInvitation.isPendingInvitation = false;
                validation.email = [''];
                break;
            }
            default:
                break;
        }
        this.setState({
            userInvitation: userInvitation,
            validation: this.state.validation,
            hasPendingInvitationVerificationDone: false,
        });
    }

    hasExistingInvitationUnMatchesToNew = (dbInvitationModel: IInvitationModel) => {
        let unMatched = false;
        const currentInvitation = this.state.userInvitation;
        if (
            dbInvitationModel != null &&
            (dbInvitationModel.firstName?.trim().toLocaleLowerCase() !=
                currentInvitation.firstName?.trim().toLocaleLowerCase() ||
                dbInvitationModel.lastName?.trim().toLocaleLowerCase() !=
                    currentInvitation.lastName?.trim().toLocaleLowerCase() ||
                dbInvitationModel.homeOrganization?.guid !=
                    currentInvitation.homeOrganization?.guid)
        ) {
            unMatched = true;
        }

        return unMatched;
    };

    verifyInvitationAndSetDetails = async () => {
        const userInvitation = this.state.userInvitation;
        if (!this.validateUserInvitation()) {
            if (userInvitation.email) {
                userInvitation.email = userInvitation.email.replace(/\s/g, '').trim();
            }
            if (this.state.hasPendingInvitationVerificationDone) {
                this.setState({ openOrganizationRolesModal: true, isNewZone: true });
            } else {
                let displayMessage = `An invitation already exists for the email address '${userInvitation.email}'. You can click on “Send Invite” to resend the invite or update the invitation and send an updated invitation email. \r\n`;

                const unMatchedMessage =
                    'However, one or more fields of the new invitation do not match the previous invitation. Please review the details before resending the invite.';
                const response = await _apiClient.getUserInvitationByEmail(userInvitation);
                if (response) {
                    this.setState({
                        hasPendingInvitationVerificationDone: true,
                        dialogClassName: 'send-invite-pending-invitation-dialog',
                    });
                    if (response.httpResponse.status == 401) {
                        window.location.reload();
                        return;
                    }
                    if (response.errorMessage) {
                        this.setState({
                            validation: ValidateUtils.parseErrors(
                                response.errors,
                                response.errorMessage
                            ),
                        });
                    } else {
                        if (response.payload) {
                            if (response.payload?.data?.isPendingInvitation) {
                                const invitationData = response.payload?.data ?? userInvitation;

                                if (this.hasExistingInvitationUnMatchesToNew(invitationData)) {
                                    displayMessage = displayMessage + ' ' + unMatchedMessage;
                                    this.loadZones(invitationData.homeOrganization?.guid ?? '');
                                    //Keep the old values only.
                                }
                                this.setState((prevState) => ({
                                    dialog: {
                                        isShowingModal:
                                            response.payload?.data?.isPendingInvitation ?? false,
                                        title: 'Confirmation',
                                        body: displayMessage,
                                        confirmText: 'Ok',
                                        isShowCancelBtn: false,
                                    },
                                    isShowSuccessMsg: false,
                                    validation: {},
                                    userInvitation: invitationData,
                                }));
                            } else {
                                this.setState({
                                    openOrganizationRolesModal: true,
                                    isNewZone: true,
                                });
                            }
                        } else {
                            this.setState({ openOrganizationRolesModal: true, isNewZone: true });
                        }
                    }
                }
            }
        }
    };

    validateUserInvitation = () => {
        const userInvitation = this.state.userInvitation;
        const validation: IValidation = {};
        let hasErrors = false;
        if (!userInvitation.firstName) {
            validation.firtsName = ['First Name is required'];
            hasErrors = true;
        }
        if (!userInvitation.lastName) {
            validation.lastName = ['Last Name is required'];
            hasErrors = true;
        }
        if (!userInvitation.email) {
            validation.email = ['Email is required'];
            hasErrors = true;
        }

        if (!userInvitation.organizationRoles || userInvitation.organizationRoles.length == 0) {
            validation.zoneAccess = ['At least one Zone Access is required'];
            hasErrors = true;
        }

        if (hasErrors) {
            this.setState({ validation: validation });
            return;
        }
        return hasErrors;
    };

    sendInvite = async () => {
        const userInvitation = this.state.userInvitation;
        if (!this.validateUserInvitation()) {
            if (userInvitation.email) {
                userInvitation.email = userInvitation.email.replace(/\s/g, '').trim();
            }
            const response = await _apiClient.createInvitation(userInvitation);

            if (response.httpResponse.status == 401) {
                window.location.reload();
                return;
            }
            if (response.errorMessage) {
                this.setState({
                    validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                });
            } else {
                if (response.payload?.data?.isPendingInvitation) {
                    this.setState((prevState) => ({
                        dialog: {
                            isShowingModal: true,
                            title: 'Confirmation',
                            body: 'This user already has a pending invitation.',
                            confirmText: 'Ok',
                            isShowCancelBtn: false,
                        },
                        isShowSuccessMsg: false,
                        validation: {},
                        userInvitation: {
                            ...prevState.userInvitation,
                            isPendingInvitation: response.payload?.data?.isPendingInvitation,
                        },
                    }));
                } else {
                    this.setState({
                        dialog: {
                            isShowingModal: true,
                            title: 'Confirmation',
                            body: 'Invitation sent successfully, Do you want to continue to send another invite?',
                            confirmText: 'Yes',
                            cancelText: 'No',
                            isShowCancelBtn: true,
                        },
                        isShowSuccessMsg: true,
                        validation: {},
                        userInvitation: { organizationRoles: [], isPendingInvitation: false },
                    });
                }
            }
        }
    };

    cancelInvite = () => {
        this.setState((prevState) => ({
            dialog: {
                isShowingModal: true,
                title: 'Confirmation',
                body: 'Are you sure you want to cancel invite?',
                confirmText: 'OK',
                cancelText: 'Cancel',
            },
            isShowSuccessMsg: false,
            userInvitation: {
                ...prevState.userInvitation,
                isPendingInvitation: false,
            },
        }));
    };

    handleConfirm = () => {
        if (this.state.userInvitation?.isPendingInvitation) {
            this.setState({
                dialog: { isShowingModal: false },
            });
            return;
        } else {
            if (this.state.isShowSuccessMsg) {
                this.setState({
                    userInvitation: { organizationRoles: [] },
                    dialog: { isShowingModal: false },
                });
                return;
            }
            this.setState({ redirect: true });
        }
    };

    handleClose = () => {
        if (this.state.isShowSuccessMsg) {
            this.setState({ redirect: true });
            return;
        }
        this.setState({ dialog: { isShowingModal: false } });
    };

    saveUserOrganization = (organizationRole: IOrganizationRoleModel) => {
        this.setState((prevState) => {
            const userInvitation = { ...prevState.userInvitation };

            // fallback to [] if not initialized
            const roles = [...(userInvitation.organizationRoles ?? [])];

            const idx = roles.findIndex((x) => x.guid === organizationRole.guid);

            if (idx !== -1) {
                roles[idx] = organizationRole;
            } else {
                roles.push(organizationRole);
            }

            userInvitation.organizationRoles = roles;

            return {
                userInvitation,
                openOrganizationRolesModal: false,
            };
        });
    };

    removeSelectedOrganizationRole = (
        event: React.FormEvent<HTMLButtonElement>,
        organizationRole: IOrganizationRoleModel
    ) => {
        const userInvitation = this.state.userInvitation;
        if (!userInvitation.organizationRoles) return;

        const matchIndex = userInvitation.organizationRoles.findIndex(
            (x) => x.guid === organizationRole.guid
        );
        if (matchIndex >= 0) userInvitation.organizationRoles.splice(matchIndex, 1);

        this.setState({ userInvitation: userInvitation });
    };

    render() {
        if (this.state.redirect) {
            return <Navigate to="/" />;
        }
        return (
            <PageWithAffixedHeader
                headerComponent={
                    <>
                        <div className="row mb-3">
                            <h1 className="col-sm-6">User Invitation</h1>
                            <div className="col-sm-6">
                                <input
                                    type="button"
                                    className="btn btn-default float-end"
                                    onClick={this.cancelInvite}
                                    value="Cancel"
                                />
                                <input
                                    type="button"
                                    className="btn btn-orange float-end"
                                    onClick={this.sendInvite}
                                    value="Send Invite"
                                />
                            </div>
                        </div>
                    </>
                }
            >
                <div className="text-danger mb-4">{this.state.validation.model}</div>
                <div className="row mb-2">
                    <label className="control-label col-sm-2" htmlFor="firstName">
                        First Name*
                    </label>
                    <div className="col-sm-10">
                        <input
                            className="form-control"
                            type="text"
                            name="firstName"
                            value={this.state.userInvitation.firstName || ''}
                            onChange={this.changeValue}
                        />
                        <span className="text-danger">{this.state.validation.firstname}</span>
                    </div>
                </div>
                <div className="row mb-2">
                    <label className="control-label col-sm-2" htmlFor="lastName">
                        Last Name*
                    </label>
                    <div className="col-sm-10">
                        <input
                            className="form-control"
                            type="text"
                            name="lastName"
                            value={this.state.userInvitation.lastName || ''}
                            onChange={this.changeValue}
                        />
                        <span className="text-danger">{this.state.validation.lastname}</span>
                    </div>
                </div>
                <div className="row mb-2">
                    <label className="control-label col-sm-2" htmlFor="email">
                        Email Address*
                    </label>
                    <div className="col-sm-10">
                        <input
                            className="form-control"
                            type="email"
                            name="email"
                            value={this.state.userInvitation.email || ''}
                            onChange={this.changeValue}
                        />
                        <span className="text-danger">{this.state.validation.email}</span>
                    </div>
                </div>
                <div className="row mb-2">
                    <label className="control-label col-sm-2" htmlFor="homeOrganization">
                        Organization*
                    </label>
                    <div className="col-sm-10">
                        <CheckmateSelect
                            options={CheckmateSelectHelper.getOrgOptions(
                                this.state.organizationsList.filter(
                                    (x) =>
                                        x.organizationType &&
                                        x.organizationType.id !==
                                            OrganizationTypesEnum.Portfolio.Value
                                )
                            )}
                            isDisabled={
                                Auth.isSystemAdmin(this.props.user) ||
                                Auth.isMasterAdmin(this.props.user)
                                    ? false
                                    : true
                            }
                            name="homeOrganization"
                            value={CheckmateSelectHelper.getSelectedOrgValue(
                                this.state.organizationsList,
                                this.state.userInvitation.homeOrganization
                                    ? this.state.userInvitation.homeOrganization.guid || ''
                                    : ''
                            )}
                            onChange={(e: any) => {
                                this.handleCheckmateSelectChange(e, 'homeOrganization');
                            }}
                        />
                    </div>
                    <span className="text-danger">{this.state.validation.homeOrganization}</span>
                </div>

                {this.state.userInvitation.homeOrganization ? (
                    <div className="row mb-2">
                        <div className="col-sm-2">
                            <label className="control-label" htmlFor="phones">
                                Zone Access
                            </label>
                            <button
                                className="btn btn-default btn-icon ms-2"
                                onClick={this.verifyInvitationAndSetDetails}
                            >
                                <i className="fal fa-lg fa-plus" />
                            </button>
                        </div>
                        <div>
                            {this.state.userInvitation &&
                            this.state.hasPendingInvitationVerificationDone &&
                            this.state.userInvitation.organizationRoles &&
                            this.state.userInvitation.organizationRoles.length > 0 ? (
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Role</th>
                                            <th>Data Scope</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.userInvitation.organizationRoles.map(
                                            (organizationRole: IOrganizationRoleModel) => {
                                                return (
                                                    <tr key={organizationRole.guid}>
                                                        <td>{organizationRole.displayName}</td>
                                                        <td>
                                                            {organizationRole.role
                                                                ? organizationRole.role.displayName
                                                                : ''}
                                                        </td>
                                                        <td>
                                                            {organizationRole.dataScope
                                                                ? organizationRole.dataScope
                                                                      .displayName
                                                                : ''}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-no-bg float-end"
                                                                onClick={(
                                                                    e: React.MouseEvent<HTMLButtonElement>
                                                                ) => {
                                                                    this.removeSelectedOrganizationRole(
                                                                        e,
                                                                        organizationRole
                                                                    );
                                                                }}
                                                            >
                                                                <i className="fal fa-lg fa-trash-alt"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-no-bg float-end"
                                                                onClick={() => {
                                                                    this.setState({
                                                                        currentOrganizationRole:
                                                                            organizationRole,
                                                                        openOrganizationRolesModal: true,
                                                                        isNewZone: false,
                                                                    });
                                                                }}
                                                            >
                                                                <i className="fal fa-lg fa-pen" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )}
                                    </tbody>
                                </table>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {this.state.openOrganizationRolesModal && (
                    <OrganizationRoleModal
                        homeOrganization={this.state.userInvitation.homeOrganization || {}}
                        isLoadingZones={this.state.isLoadingZones}
                        isNew={this.state.isNewZone ?? true}
                        onCancel={() => this.setState({ openOrganizationRolesModal: false })}
                        onSave={this.saveUserOrganization}
                        open={this.state.openOrganizationRolesModal || false}
                        zones={this.state.zones}
                        zonesCurrentlyAssigned={this.state.userInvitation.organizationRoles}
                        currentOrganizationRole={this.state.currentOrganizationRole}
                    />
                )}

                <CheckmateDialog
                    isShowingModal={this.state.dialog.isShowingModal}
                    body={this.state.dialog.body}
                    handleClose={this.handleClose}
                    handleConfirm={this.handleConfirm}
                    confirmText={this.state.dialog.confirmText}
                    cancelText={this.state.dialog.cancelText}
                    confirmButtonClassName="btn btn-black"
                    dialogClassName={this.state.dialogClassName}
                    closeButtonClassName="btn btn-default"
                    isShowCancel={this.state.dialog.isShowCancelBtn}
                    showBackdrop
                />
            </PageWithAffixedHeader>
        );
    }
}
